import type {
  UpdateCallOffItemResponse,
  UpdateCallOffItemVariables,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import {
  zodUpdateCallOffItemPayloadSchema,
  zodUpdateCallOffItemResponseSchema,
} from './validations';

/**
 * Updates an existing line item in a call-off (partial update - only provided fields are updated).
 *
 * @param variables - The call-off ID, item ID, and data to update.
 * @returns The updated call-off item with its metadata.
 * @throws Error if the API request fails or validation fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Offs/operation/update_calloff_item_calloffs__calloff_id__items__item_id__put
 */
export const updateCallOffItem = async (
  variables: UpdateCallOffItemVariables,
) => {
  const { callOffId, itemId, payload } = variables;

  const response = await vestigasApi
    .put(ENDPOINT.CALL_OFF.UPDATE_ITEM(callOffId, itemId), {
      json: payload,
      validate: {
        body: zodUpdateCallOffItemPayloadSchema,
      },
    })
    .json<UpdateCallOffItemResponse>();

  // Validate the response with Zod
  const validatedResponse =
    zodUpdateCallOffItemResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Update CallOff Item API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
